import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Paperclip, Loader2, X, ExternalLink } from 'lucide-react';
import useCoachingResources, { COACHING_EVENT, coachingPageUrl } from '../hooks/useCoachingResources';
import { compressImage } from '../utils/imageCompress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { title: '', group: '', body: '', file: null, removeFile: false };
const input = 'w-full font-body text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-forest';

const ResourceForm = ({ initial, groups, onCancel, onSave, saving }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="space-y-2 bg-[#faf6ef] border border-[#e2d4bf] rounded-xl p-3" data-testid="coaching-form">
      <input value={f.title} onChange={set('title')} placeholder="Title (e.g. Grounded Burner)" className={input} required data-testid="coaching-form-title" />
      <input value={f.group} onChange={set('group')} placeholder="Group (e.g. Metabolic Typing)" list="coaching-groups" className={input} data-testid="coaching-form-group" />
      <datalist id="coaching-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
      <textarea value={f.body} onChange={set('body')} rows={6} placeholder="Write or paste the text your client will read…" className={input} data-testid="coaching-form-body" />
      <div className="flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-1.5 text-xs font-body text-forest cursor-pointer bg-white border border-stone-200 rounded-full px-3 py-1.5 hover:border-forest/40">
          <Paperclip size={13} /> {f.file ? f.file.name : initial.fileName && !f.removeFile ? `Replace ${initial.fileName}` : 'Attach a file (PDF, image…)'}
          <input type="file" className="hidden" accept=".pdf,image/*,video/*,audio/*,.doc,.docx" onChange={(e) => setF((p) => ({ ...p, file: e.target.files?.[0] || null, removeFile: false }))} data-testid="coaching-form-file" />
        </label>
        {initial.fileName && !f.file && (
          <label className="inline-flex items-center gap-1 text-xs font-body text-stone-500">
            <input type="checkbox" checked={f.removeFile} onChange={(e) => setF((p) => ({ ...p, removeFile: e.target.checked }))} data-testid="coaching-form-remove-file" /> Remove current file
          </label>
        )}
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="text-xs font-body text-stone-500 px-3 py-1.5" data-testid="coaching-form-cancel">Cancel</button>
        <button type="submit" disabled={saving} className="inline-flex items-center gap-1.5 bg-forest text-cream text-xs font-body px-4 py-1.5 rounded-full disabled:opacity-60" data-testid="coaching-form-save">
          {saving && <Loader2 size={12} className="animate-spin" />} Save
        </button>
      </div>
    </form>
  );
};

export default function CoachingResourceManager({ auth, onClose }) {
  const { resources } = useCoachingResources();
  const [editing, setEditing] = useState(null); // 'new' | resource id
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const groups = [...new Set(resources.map((r) => r.group).filter(Boolean))];
  const qs = `admin_email=${encodeURIComponent(auth.email)}&admin_password=${encodeURIComponent(auth.password)}`;

  const save = async (f) => {
    setSaving(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('title', f.title); fd.append('group', f.group); fd.append('body', f.body);
      if (f.file) fd.append('file', f.file.type.startsWith('image/') ? await compressImage(f.file) : f.file);
      if (editing !== 'new') fd.append('remove_file', f.removeFile ? 'true' : 'false');
      const url = editing === 'new' ? `${API}/coaching-resources?${qs}` : `${API}/coaching-resources/${editing}?${qs}`;
      const res = await fetch(url, { method: editing === 'new' ? 'POST' : 'PUT', body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Save failed');
      setEditing(null);
      window.dispatchEvent(new Event(COACHING_EVENT));
    } catch (e) { setErr(e.message || 'Save failed — please try again.'); }
    setSaving(false);
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? Clients who had it in their plan will no longer see it.`)) return;
    await fetch(`${API}/coaching-resources/${r.id}?${qs}`, { method: 'DELETE' });
    window.dispatchEvent(new Event(COACHING_EVENT));
  };

  return (
    <div className="mt-2 bg-white border border-[#e2d4bf] rounded-xl p-4" data-testid="coaching-manager">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-body text-sm text-forest font-medium">Nutrition & Wellness Coaching resources</p>
          <p className="font-body text-xs text-stone-500">Write the text, attach a file, or both. They appear in the tick-list for every client and can be attached to quiz results.</p>
        </div>
        <button onClick={onClose} className="text-stone-400 hover:text-forest" data-testid="coaching-manager-close"><X size={16} /></button>
      </div>
      {err && <p className="text-xs font-body text-red-600 mb-2" data-testid="coaching-manager-error">{err}</p>}
      <div className="space-y-1.5">
        {resources.map((r) => (editing === r.id ? (
          <ResourceForm key={r.id} initial={{ ...EMPTY, title: r.title, group: r.group, body: r.body, fileName: r.file_name }} groups={groups} onCancel={() => setEditing(null)} onSave={save} saving={saving} />
        ) : (
          <div key={r.id} className="flex items-center gap-2 py-1.5 border-b border-stone-100 last:border-0" data-testid={`coaching-row-${r.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-body text-sm text-forest truncate">{r.title}</p>
              <p className="font-body text-[11px] text-stone-500 truncate">
                {r.group || 'No group'} · {r.body ? 'text' : 'no text yet'}{r.has_file ? ` · ${r.file_name}` : ''}
              </p>
            </div>
            <a href={coachingPageUrl(r.id)} target="_blank" rel="noopener noreferrer" className="p-1.5 text-stone-400 hover:text-forest" title="Preview" data-testid={`coaching-preview-${r.id}`}><ExternalLink size={14} /></a>
            <button onClick={() => setEditing(r.id)} className="p-1.5 text-stone-400 hover:text-forest" title="Edit" data-testid={`coaching-edit-${r.id}`}><Pencil size={14} /></button>
            <button onClick={() => remove(r)} className="p-1.5 text-stone-400 hover:text-red-600" title="Delete" data-testid={`coaching-delete-${r.id}`}><Trash2 size={14} /></button>
          </div>
        )))}
      </div>
      {editing === 'new' ? (
        <div className="mt-3"><ResourceForm initial={EMPTY} groups={groups} onCancel={() => setEditing(null)} onSave={save} saving={saving} /></div>
      ) : (
        <button onClick={() => setEditing('new')} className="mt-3 inline-flex items-center gap-1.5 text-xs font-body text-forest border border-forest/30 rounded-full px-3 py-1.5 hover:bg-forest hover:text-cream transition-colors" data-testid="coaching-add">
          <Plus size={13} /> Add a resource
        </button>
      )}
    </div>
  );
}
